import { apiRequest } from "src/services/apiClient";

/** Staff-facing device-credential management (Settings → Praxis Software Sync). The device
 *  itself (the future desktop agent) never calls these — see backend/src/routes/deviceAuthRoutes.ts
 *  for its own separate, session-less endpoints. */
export interface DeviceDTO {
    id: string;
    label: string;
    createdAt: string;
    lastRenewedAt: string;
    lastSeenAt: string | null;
    expiresAt: string;
    revoked: boolean;
    revokedAt: string | null;
    /** Credential state only — a device can be "active" while its agent isn't running. */
    status: "active" | "expiring_soon" | "expired" | "revoked";
    /** A valid credential that checked in within the last 15 minutes: the agent is actually running. */
    connected: boolean;
    /** "This is a shared PC" — off by default. On, the agent's upload wizard shows a doctor
     *  picker per import instead of defaulting to whoever enrolled the device. */
    sharedDevice: boolean;
    /** Desktop app version, from the heartbeat. */
    clientVersion: string | null;
    /** Practice software and version as staff named them on the app's setup page (app 1.0.4+). */
    practiceSoftware: string | null;
    practiceSoftwareVersion: string | null;
    /** What the last GDT file the app read said about itself (app 1.0.4+). */
    gdtSource: {
        gdtVersion?: string;
        charsetDeclared?: string;
        charsetUsed?: string;
        senderId?: string;
        seenAt?: string;
    } | null;
}

export function listDevices(): Promise<DeviceDTO[]> {
    return apiRequest<DeviceDTO[]>("/devices");
}

export function createEnrollmentToken(): Promise<{ token: string; expiresAt: string }> {
    return apiRequest<{ token: string; expiresAt: string }>(
        "/devices/enrollment-token",
        { method: "POST" },
    );
}

export function createRenewalToken(deviceId: string): Promise<{ token: string; expiresAt: string }> {
    return apiRequest<{ token: string; expiresAt: string }>(
        `/devices/${deviceId}/renewal-token`,
        { method: "POST" },
    );
}

export function revokeDevice(deviceId: string): Promise<{ ok: boolean }> {
    return apiRequest<{ ok: boolean }>(
        `/devices/${deviceId}/revoke`,
        { method: "POST" },
    );
}

/** Hard delete — only valid server-side for a device that never connected (lastSeenAt null).
 *  A device with any real history must be revoked instead; the backend enforces this itself. */
export function deleteDevice(deviceId: string): Promise<{ ok: boolean }> {
    return apiRequest<{ ok: boolean }>(
        `/devices/${deviceId}`,
        { method: "DELETE" },
    );
}

export function setDeviceSharedFlag(deviceId: string, sharedDevice: boolean): Promise<{ ok: boolean; sharedDevice: boolean }> {
    return apiRequest<{ ok: boolean; sharedDevice: boolean }>(
        `/devices/${deviceId}/shared`,
        { method: "PATCH", body: JSON.stringify({ sharedDevice }) },
    );
}
